import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { uploadVideoFile, deleteVideoFile, fetchVideoProgressApi, trimVideoFile, copyVideoFile, fetchVideoUploadInitApi } from '../../Service/VideoIndex_API/videoIndexApi';

export const fetchVideoUploadInit = createAsyncThunk(
  'adminVideoUpload/fetchVideoUploadInit',
  async (moduleId, { rejectWithValue }) => {
    try {
      const data = await fetchVideoUploadInitApi(moduleId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch video upload initialization data.');
    }
  }
);

export const uploadVideo = createAsyncThunk(
  'adminVideoUpload/uploadVideo',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await uploadVideoFile(formData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to upload video.');
    }
  }
);

export const copyVideo = createAsyncThunk(
  'adminVideoUpload/copyVideo',
  async (copyFormData, { rejectWithValue }) => {
    try {
      const data = await copyVideoFile(copyFormData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to copy video.');
    }
  }
);

export const deleteVideo = createAsyncThunk(
  'adminVideoUpload/deleteVideo',
  async ({ videoId }, { rejectWithValue }) => {
    try {
      const data = await deleteVideoFile(videoId);
      return { videoId, data };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete video.');
    }
  }
);

export const fetchVideoProgress = createAsyncThunk(
  'adminVideoUpload/fetchVideoProgress',
  async ({ courseId, moduleId, videoId }, { rejectWithValue }) => {
    try {
      const data = await fetchVideoProgressApi(videoId);
      return { videoId, progress: data.progress };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch video progress.');
    }
  }
);

export const trimVideo = createAsyncThunk(
  'adminVideoUpload/trimVideo',
  async (trimData, { rejectWithValue }) => {
    try {
      const data = await trimVideoFile(trimData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to trim video.');
    }
  }
);

const videoUploadSlice = createSlice({
  name: 'adminVideoUpload',
  initialState: {
    progressByVideoId: {},
    uploading: false,
    copying: false,
    trimming: false,
    deleting: false,
    loadingInit: false,
    course: null,
    module: null,
    next_vid_cat_id: 1,
    error: null
  },
  reducers: {
    clearUploadProgress: (state, action) => {
      const videoId = action.payload;
      delete state.progressByVideoId[videoId];
    },
    clearAllProgress: (state) => {
      state.progressByVideoId = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVideoUploadInit.pending, (state) => {
        state.loadingInit = true;
        state.error = null;
      })
      .addCase(fetchVideoUploadInit.fulfilled, (state, action) => {
        state.loadingInit = false;
        state.course = action.payload.course;
        state.module = action.payload.module;
        state.next_vid_cat_id = action.payload.next_vid_cat_id;
      })
      .addCase(fetchVideoUploadInit.rejected, (state, action) => {
        state.loadingInit = false;
        state.error = action.payload;
      })
      .addCase(uploadVideo.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.uploading = false;
        // set progress to 5% initially to start polling
        const videoId = action.payload.video_id;
        state.progressByVideoId[videoId] = 5;
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload;
      })
      .addCase(copyVideo.pending, (state) => {
        state.copying = true;
        state.error = null;
      })
      .addCase(copyVideo.fulfilled, (state) => {
        state.copying = false;
      })
      .addCase(copyVideo.rejected, (state, action) => {
        state.copying = false;
        state.error = action.payload;
      })
      .addCase(deleteVideo.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.deleting = false;
        const { videoId } = action.payload;
        delete state.progressByVideoId[videoId];
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })
      .addCase(fetchVideoProgress.fulfilled, (state, action) => {
        const { videoId, progress } = action.payload;
        state.progressByVideoId[videoId] = progress;
      })
      .addCase(trimVideo.pending, (state) => {
        state.trimming = true;
        state.error = null;
      })
      .addCase(trimVideo.fulfilled, (state) => {
        state.trimming = false;
      })
      .addCase(trimVideo.rejected, (state, action) => {
        state.trimming = false;
        state.error = action.payload;
      });
  }
});

// Actions
export const { clearUploadProgress, clearAllProgress } = videoUploadSlice.actions;

// Selectors
export const selectVideoUpload = (state) => state.videoUpload;

export default videoUploadSlice.reducer;
